import type { InfiniteData, QueryClient } from "@tanstack/react-query"
import { getLocale } from "@/shared/lib/i18n"
import { streamKeys } from "../model/keys"
import type { StreamMessage, StreamPage } from "../model/types"

type StreamData = InfiniteData<StreamPage>

export function appendStreamMessage(
	queryClient: QueryClient,
	message: StreamMessage,
): void {
	queryClient.setQueriesData<StreamData>(
		{ queryKey: streamKeys.messages() },
		(old) => {
			const [latest, ...older] = old?.pages ?? []
			if (!old || !latest) return old
			const exists = old.pages.some((page) =>
				page.items.some((item) => item.id === message.id),
			)
			if (exists) return old
			return {
				...old,
				pages: [{ ...latest, items: [...latest.items, message] }, ...older],
			}
		},
	)
}

export function patchStreamMessage(
	queryClient: QueryClient,
	messageId: number,
	patch: (message: StreamMessage) => StreamMessage,
): void {
	queryClient.setQueriesData<StreamData>(
		{ queryKey: streamKeys.messages() },
		(old) => {
			if (!old) return old
			return {
				...old,
				pages: old.pages.map((page) => ({
					...page,
					items: page.items.map((item) => {
						if (item.id !== messageId) return item
						return patch(item)
					}),
				})),
			}
		},
	)
}

export function isStreamMessageStarred(
	queryClient: QueryClient,
	messageId: number,
): boolean {
	return queryClient
		.getQueriesData<StreamData>({ queryKey: streamKeys.messages() })
		.some(([, data]) =>
			data?.pages.some((page) =>
				page.items.some((item) => item.id === messageId && item.starred),
			),
		)
}

export async function removeStreamMessage(
	queryClient: QueryClient,
	messageId: number,
): Promise<void> {
	await queryClient.cancelQueries({ queryKey: streamKeys.messages() })
	queryClient.setQueriesData<StreamData>(
		{ queryKey: streamKeys.messages() },
		(old) => {
			if (!old) return old
			return {
				...old,
				pages: old.pages.map((page) => ({
					...page,
					items: page.items.filter((item) => item.id !== messageId),
				})),
			}
		},
	)
}

export function setStreamHasNew(queryClient: QueryClient, hasNew: boolean) {
	queryClient.setQueryData(streamKeys.status(getLocale()), hasNew)
}
